import React, { useEffect, useRef, useState } from 'react'
import {
  FiActivity,
  FiArrowRight,
  FiBarChart2,
  FiBookOpen,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiCoffee,
  FiCompass,
  FiFeather,
  FiGlobe,
  FiHeart,
  FiMic,
  FiMessageCircle,
  FiSliders,
  FiTruck,
  FiVolume2,
} from 'react-icons/fi'
import { TbDeviceVisionPro } from 'react-icons/tb'
import AnimatedBrandMark from '../AnimatedBrandMark'
import FinalCtaPanel from '../FinalCtaPanel'
import { APP_ENTRY_HREF } from '../../constants/navigation'
import practiceScenes from '../../data/practiceScenes'
import './scenes.css'
import cafeAppPreview from '../../assets/scenes/cafe-erasmus-app-preview.webp'
import library from '../../assets/scenes/library-with-people.webp'
import park from '../../assets/scenes/park-with-people.webp'
import metro from '../../assets/scenes/metro-with-people.webp'
import cafeEnvironment from '../../assets/scenes/cafe-erasmus.webp'
import journeyWelcome from '../../assets/scenes/journey-welcome-premium.webp'
import journeyDebrief from '../../assets/scenes/journey-debrief-premium.webp'

const immersiveEnvironments = [
  {
    id: 'library',
    icon: FiBookOpen,
    image: library,
    title: 'Bibliothèque de nuit',
    titleEn: 'Library after dark',
    mood: 'Révisions · parler à voix basse',
    moodEn: 'Study · speak quietly',
  },
  {
    id: 'cafe',
    icon: FiCoffee,
    image: cafeAppPreview,
    title: 'Café Erasmus',
    titleEn: 'Erasmus Café',
    mood: 'Convivial & social',
    moodEn: 'Friendly & social',
  },
  {
    id: 'park',
    icon: FiFeather,
    image: park,
    title: 'Retrouvailles au parc',
    titleEn: 'Meeting up in the park',
    mood: 'Promenade du soir · confidences',
    moodEn: 'Evening walk · catching up',
  },
  {
    id: 'metro',
    icon: FiTruck,
    image: metro,
    title: 'Dernier métro',
    titleEn: 'Last metro home',
    mood: 'Annonce · correspondance',
    moodEn: 'Announcement · transfer',
  },
]

const practiceSceneIcons = {
  professional: FiMic,
  heritage: FiGlobe,
  city: FiCompass,
  narrative: FiFeather,
  'cultural-visits': FiBookOpen,
  everyday: FiMessageCircle,
}

const practiceSceneCases = {
  professional: {
    title: 'Pitch aux investisseurs',
    titleEn: 'Investor pitch',
    mood: 'Start-up · questions-réponses',
    moodEn: 'Startup · investor Q&A',
  },
  heritage: {
    title: 'Veillée de contes dans un riad',
    titleEn: 'Storytelling evening in a riad',
    mood: 'Tradition orale · thé partagé',
    moodEn: 'Oral tradition · shared tea',
  },
  city: {
    title: 'Demander son chemin au marché',
    titleEn: 'Asking for directions at the market',
    mood: 'Vieille ville · itinéraire',
    moodEn: 'Old town · directions',
  },
  narrative: {
    title: 'Le Nom de la rose',
    titleEn: 'The Name of the Rose',
    mood: 'Bibliothèque interdite · énigme',
    moodEn: 'Forbidden library · mystery',
  },
  'cultural-visits': {
    title: 'Tate Modern, Londres',
    titleEn: 'Tate Modern, London',
    mood: 'Visite guidée · art contemporain',
    moodEn: 'Guided tour · contemporary art',
  },
  everyday: {
    title: 'Commander un croissant',
    titleEn: 'Ordering a croissant',
    mood: 'Au comptoir · petite conversation',
    moodEn: 'At the counter · small talk',
  },
}

const scenes = [
  ...immersiveEnvironments,
  ...practiceScenes.map((scene) => {
    const sceneCase = practiceSceneCases[scene.id]

    return {
      id: scene.id,
      icon: practiceSceneIcons[scene.id],
      image: scene.image,
      imageSmall: scene.imageSmall,
      title: sceneCase.title,
      titleEn: sceneCase.titleEn,
      mood: sceneCase.mood,
      moodEn: sceneCase.moodEn,
    }
  }),
]

const journey = [
  {
    id: 'welcome',
    icon: FiCompass,
    image: journeyWelcome,
    imageAlt: 'A luminous neural guide opening a portal into a warm European café scene',
    kicker: 'Entrez',
    kickerEn: 'Enter',
    title: 'Accueil immersif',
    titleEn: 'Immersive welcome',
    insideTitle: 'Vous entrez dans une situation, pas dans un cours.',
    insideTitleEn: 'You enter a situation—not a lesson.',
    insideText: 'Le lieu, l’ambiance et la voix créent un contexte familier. Vous comprenez où vous êtes, ce qui se passe et pourquoi parler.',
    insideTextEn: 'Place, atmosphere and voice create a familiar context. You understand where you are, what is happening and why it is worth speaking.',
    highlights: [
      ['Repères visuels immédiats', 'Immediate visual context'],
      ['Voix d’accueil naturelle', 'A natural guiding voice'],
      ['Entrée sans pression', 'A pressure-free arrival'],
    ],
  },
  {
    id: 'settings',
    icon: FiSliders,
    image: cafeEnvironment,
    imageAlt: 'A warm, intimate café prepared for a personalized language session',
    kicker: 'Personnalisez',
    kickerEn: 'Personalize',
    title: 'Votre session',
    titleEn: 'Your session',
    insideTitle: 'La bonne dose de défi, avant d’entrer.',
    insideTitleEn: 'The right level of challenge, before you begin.',
    insideText: 'Vous composez une expérience qui respecte votre rythme. Les réglages restent discrets pour laisser toute la place à la conversation.',
    insideTextEn: 'You shape an experience that respects your pace. Settings stay discreet so the conversation can take center stage.',
    highlights: [
      ['Langue et niveau', 'Language and level'],
      ['Confiance déclarée', 'Self-set confidence'],
      ['Aide douce ajustable', 'Adjustable gentle help'],
    ],
  },
  {
    id: 'immersion',
    icon: FiMessageCircle,
    image: cafeAppPreview,
    imageAlt: 'Two people having a natural conversation inside Café Erasmus',
    kicker: 'Vivez',
    kickerEn: 'Experience',
    title: 'Conversation vivante',
    titleEn: 'Living conversation',
    insideTitle: 'À l’intérieur de Café Erasmus',
    insideTitleEn: 'Inside Café Erasmus',
    insideText: 'La conversation avance comme dans la vie réelle. Vous répondez avec vos mots, recevez un indice seulement si nécessaire et restez immergé dans le moment.',
    insideTextEn: 'The conversation moves like real life. You answer in your own words, receive a cue only when needed and stay immersed in the moment.',
    highlights: [
      ['Écoutez le contexte', 'Listen to the context'],
      ['Choisissez une intention', 'Choose an intention'],
      ['Parlez à votre rythme', 'Speak at your pace'],
    ],
  },
  {
    id: 'debrief',
    icon: FiActivity,
    image: journeyDebrief,
    imageAlt: 'A speech waveform reconnecting illuminated memory pathways in a neural network',
    kicker: 'Progressez',
    kickerEn: 'Progress',
    title: 'Débrief intelligent',
    titleEn: 'Smart debrief',
    insideTitle: 'Vous voyez ce qui vient de se reconnecter.',
    insideTitleEn: 'See what has just reconnected.',
    insideText: 'Un retour clair met en lumière les expressions mobilisées, les aides utilisées et la prochaine scène qui peut consolider votre élan.',
    insideTextEn: 'A clear recap highlights the expressions you used, the support you needed and the next scene that can reinforce your momentum.',
    highlights: [
      ['Expressions retrouvées', 'Recalled expressions'],
      ['Aides rendues visibles', 'Support made visible'],
      ['Prochaine scène suggérée', 'Next scene suggested'],
    ],
  },
]

const dialogueChoices = [
  {
    icon: FiCoffee,
    fr: 'Commander un café',
    en: 'Order a coffee',
    speech: 'Hola, ¿me pone un café con leche, por favor?',
  },
  {
    icon: FiMessageCircle,
    fr: 'Répondre à un ami',
    en: 'Reply to a friend',
    speech: '¡Claro! Nos vemos aquí a las seis.',
  },
  {
    icon: FiHeart,
    fr: 'Raconter un souvenir',
    en: 'Share a memory',
    speech: 'Este lugar me recuerda a un café de Madrid.',
  },
]

const SectionTitle = ({ id, titleFr, titleEn, variant = 'centered' }) => (
  <div className={`scenes-page__section-title scenes-page__section-title--${variant}`}>
    <span aria-hidden="true" />
    <h2 id={id}>
      <span lang="fr">{titleFr}</span>
      <small lang="en">{titleEn}</small>
    </h2>
    <span aria-hidden="true" />
  </div>
)

const SceneConfigDock = ({ helpEnabled, onToggleHelp }) => (
  <aside className="scene-config-dock" aria-label="Scene preview configuration">
    <div className="scene-config-dock__item">
      <FiGlobe aria-hidden="true" />
      <span>
        <small>Langue</small>
        <strong><b>ES</b><span>Espagnol</span></strong>
      </span>
    </div>

    <div className="scene-config-dock__item">
      <FiSliders aria-hidden="true" />
      <span>
        <small>Niveau</small>
        <strong><b>B1</b><span>Intermédiaire</span></strong>
      </span>
    </div>

    <div className="scene-config-dock__brand" role="img" aria-label="OctoSynapse">
      <AnimatedBrandMark className="scene-config-dock__mark" />
    </div>

    <div className="scene-config-dock__item scene-config-dock__confidence">
      <FiActivity aria-hidden="true" />
      <span>
        <small>Confiance</small>
        <strong><b>5 / 8</b></strong>
      </span>
    </div>

    <button
      className="scene-config-dock__item scene-config-dock__help"
      type="button"
      aria-label="Toggle gentle help in scene preview"
      aria-pressed={helpEnabled}
      onClick={onToggleHelp}
    >
      <FiFeather aria-hidden="true" />
      <span>
        <small>Aide douce</small>
        <strong>
          <b>{helpEnabled ? 'ON' : 'OFF'}</b>
          <span>{helpEnabled ? 'Activée' : 'Désactivée'}</span>
        </strong>
      </span>
      <i className={helpEnabled ? 'is-on' : ''} aria-hidden="true"><b /></i>
    </button>
  </aside>
)

const JourneyVisualOverlay = ({
  activeStepId,
  activeDialogue,
  helpEnabled,
  onDialogueChange,
  onToggleHelp,
}) => {
  if (activeStepId === 'welcome') {
    return (
      <div className="journey-visual journey-visual--welcome">
        <div className="journey-ready-card">
          <span className="journey-ready-card__icon"><FiCheck aria-hidden="true" /></span>
          <span>
            <small lang="fr">Votre scène</small>
            <b lang="fr">Session prête</b>
            <em lang="en">Ready to enter</em>
          </span>
          <i aria-hidden="true" />
        </div>
        <div className="journey-context-cues" aria-label="Scene context signals">
          <span><FiCompass aria-hidden="true" /><b>Lieu</b><small>Place</small></span>
          <span><FiVolume2 aria-hidden="true" /><b>Voix</b><small>Voice</small></span>
          <span><FiHeart aria-hidden="true" /><b>Intention</b><small>Intent</small></span>
        </div>
      </div>
    )
  }

  if (activeStepId === 'settings') {
    return (
      <div className="journey-visual journey-visual--settings">
        <div className="journey-config-card">
          <div className="journey-config-card__header">
            <span>
              <small lang="fr">Votre session</small>
              <b lang="en">Your session</b>
            </span>
            <i><span /></i>
          </div>
          <div className="journey-config-card__grid">
            <span>
              <FiGlobe aria-hidden="true" />
              <small>Langue <em>Language</em></small>
              <b>ES <em>Espagnol</em></b>
            </span>
            <span>
              <FiSliders aria-hidden="true" />
              <small>Niveau <em>Level</em></small>
              <b>B1 <em>Intermédiaire</em></b>
            </span>
            <span className="journey-config-card__confidence">
              <FiActivity aria-hidden="true" />
              <small>Confiance <em>Confidence</em></small>
              <b>5 / 8</b>
              <i aria-hidden="true">
                {[0, 1, 2, 3, 4, 5, 6, 7].map((dot) => (
                  <span className={dot < 5 ? 'is-filled' : ''} key={dot} />
                ))}
              </i>
            </span>
          </div>
          <button
            className="journey-config-card__help"
            type="button"
            onClick={onToggleHelp}
            aria-pressed={helpEnabled}
          >
            <FiFeather aria-hidden="true" />
            <span>Aide douce <small>Gentle help</small></span>
            <b>{helpEnabled ? 'ON' : 'OFF'}</b>
            <i className={helpEnabled ? 'is-on' : ''} aria-hidden="true"><span /></i>
          </button>
        </div>
      </div>
    )
  }

  if (activeStepId === 'immersion') {
    return (
      <div className="journey-visual journey-visual--immersion">
        <div className="journey-dialogue" aria-label="Choose a conversation intention">
          <p><span lang="fr">Que souhaitez-vous faire ?</span><small lang="en">What would you like to do?</small></p>
          {dialogueChoices.map(({ icon: Icon, fr, en }, index) => (
            <button
              className={activeDialogue === index ? 'is-active' : ''}
              type="button"
              key={fr}
              onClick={() => onDialogueChange(index)}
              aria-pressed={activeDialogue === index}
            >
              <Icon aria-hidden="true" />
              <span lang="fr">{fr}<small lang="en">{en}</small></span>
              <FiChevronRight aria-hidden="true" />
            </button>
          ))}
        </div>
        <div className="journey-voice-card">
          <span className="journey-voice-card__avatar">OS</span>
          <span>
            <small>Votre réponse <em>Your response</em></small>
            <b lang="es">{dialogueChoices[activeDialogue].speech}</b>
            <i className="journey-waveform" aria-hidden="true">
              {[5, 11, 17, 9, 21, 13, 7, 18, 23, 12, 8, 16, 10, 6, 14, 9].map((height, index) => (
                <span key={`${height}-${index}`} style={{ '--wave-height': `${height}px` }} />
              ))}
            </i>
          </span>
          <FiMic aria-hidden="true" />
        </div>
      </div>
    )
  }

  return (
    <div className="journey-visual journey-visual--debrief">
      <div className="journey-debrief-card" aria-label="Illustrative session preview">
        <div className="journey-debrief-card__header">
          <span>
            <small lang="fr">Aperçu illustratif</small>
            <b lang="en">Illustrative session preview</b>
          </span>
          <FiBarChart2 aria-hidden="true" />
        </div>
        <div className="journey-debrief-card__stats">
          <span><b>08</b><small>Expressions <em>recalled</em></small></span>
          <span><b>02</b><small>Aides <em>gentle cues</em></small></span>
          <span><b>5→6</b><small>Confiance <em>of 8</em></small></span>
        </div>
        <div className="journey-memory-path">
          <span><i /><b>café</b></span>
          <span><i /><b>souvenir</b></span>
          <span className="is-active"><i /><b>conversation</b></span>
          <span><i /><b>prochaine scène</b></span>
        </div>
      </div>
    </div>
  )
}

const Scenes = () => {
  const [selectedId, setSelectedId] = useState('cafe')
  const [helpEnabled, setHelpEnabled] = useState(true)
  const [activeJourneyId, setActiveJourneyId] = useState('immersion')
  const [activeDialogue, setActiveDialogue] = useState(0)
  const [journeyVisible, setJourneyVisible] = useState(false)
  const journeyRef = useRef(null)
  const selectedScene = scenes.find((scene) => scene.id === selectedId)
    || scenes.find((scene) => scene.id === 'cafe')
    || scenes[0]
  const SelectedSceneIcon = selectedScene.icon
  const activeJourneyStep = journey.find((step) => step.id === activeJourneyId) || journey[2]
  const activeJourneyIndex = journey.findIndex((step) => step.id === activeJourneyStep.id)

  useEffect(() => {
    const section = journeyRef.current

    if (!section) return undefined

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setJourneyVisible(true)
      return undefined
    }

    const observer = new window.IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return

      setJourneyVisible(true)
      observer.disconnect()
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -8% 0px',
    })

    observer.observe(section)

    return () => observer.disconnect()
  }, [])

  const moveSelection = (direction) => {
    const currentIndex = scenes.findIndex((scene) => scene.id === selectedId)
    const nextIndex = (currentIndex + direction + scenes.length) % scenes.length
    setSelectedId(scenes[nextIndex].id)
  }

  return (
    <main className="scenes-page" id="scenes" tabIndex="-1">
      <section className="scenes-page__hero" aria-labelledby="scenes-title">
        <div className="scenes-page__hero-copy">
          <div className="scenes-page__eyebrow" aria-hidden="true">
            <span lang="fr">Immersion VR</span>
            <i />
            <small lang="en">Immersive scenes</small>
          </div>
          <h1 id="scenes-title">
            <span className="scenes-page__title-fr" lang="fr"><strong>Scènes</strong></span>
            <small className="scenes-page__title-en" lang="en">Scenes</small>
          </h1>
          <p className="scenes-page__hero-lead">
            <span lang="fr">Découvrez, étape par étape, comment une scène virtuelle réactive une langue oubliée.</span>
            <small lang="en">Discover step by step how a virtual scene reactivates a forgotten language.</small>
          </p>
          <div className="scenes-page__hero-note">
            <TbDeviceVisionPro aria-hidden="true" />
            <span lang="fr">Immersion en réalité virtuelle <small lang="en">Immersive VR conversation</small></span>
          </div>
        </div>

        <div className="scenes-page__hero-stage is-ready">
          <img
            key={selectedScene.id}
            className="scenes-page__hero-media scenes-page__hero-media--snapshot"
            src={selectedScene.image}
            alt={selectedScene.id === 'cafe'
              ? 'A man and woman having a friendly conversation in Café Erasmus'
              : `${selectedScene.title} immersive scene`}
            decoding="async"
            fetchPriority="high"
          />
          <div className="scenes-page__scene-label">
            <span className="scenes-page__scene-icon"><SelectedSceneIcon aria-hidden="true" /></span>
            <span>
              <span lang="fr">{selectedScene.title}</span>
              <small lang="en">{selectedScene.titleEn}</small>
            </span>
            <span className="scenes-page__scene-status"><i /> Scène active</span>
          </div>
          <div className="scenes-page__ready-badge">
            <FiCheck aria-hidden="true" />
            <span>Session prête <small>Ready to enter</small></span>
          </div>
          <SceneConfigDock
            helpEnabled={helpEnabled}
            onToggleHelp={() => setHelpEnabled((value) => !value)}
          />
        </div>
      </section>

      <section className="scenes-page__panel scenes-page__chooser" aria-labelledby="chooser-title">
        <SectionTitle
          id="chooser-title"
          titleFr={<>Choisissez une <strong>scène</strong></>}
          titleEn={<>Choose a <strong>scene</strong></>}
        />
        <p className="scenes-page__chooser-intro">
          <span lang="fr">Choisissez une scène concrète — un lieu, une œuvre ou une situation précise — puis entrez dans l’action.</span>
          <small lang="en">Choose a concrete scene—a place, a story or a specific situation—then step into the action.</small>
        </p>
        <button className="scenes-page__carousel-arrow scenes-page__carousel-arrow--left" type="button" onClick={() => moveSelection(-1)} aria-label="Previous scene"><FiChevronLeft /></button>
        <div className="scenes-page__scene-grid">
          {scenes.map(({ id, icon: Icon, image, imageSmall, title, titleEn, mood, moodEn }) => (
            <button className={`scene-card ${selectedId === id ? 'scene-card--active' : ''}`} data-scene-id={id} type="button" key={id} onClick={() => setSelectedId(id)} aria-pressed={selectedId === id}>
              <div className="scene-card__image">
                <img
                  src={image}
                  srcSet={imageSmall ? `${imageSmall} 768w, ${image} 1536w` : undefined}
                  sizes="(max-width: 620px) 44vw, (max-width: 820px) 45vw, (max-width: 1060px) 30vw, 18vw"
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                <span><Icon aria-hidden="true" /></span>
                {selectedId === id && <i><FiCheck aria-hidden="true" /></i>}
              </div>
              <span className="scene-card__copy">
                <strong>
                  <span lang="fr">{title}</span>
                  <small lang="en">{titleEn}</small>
                </strong>
                <small>
                  <span lang="fr">{mood}</span>
                  <em lang="en">{moodEn}</em>
                </small>
              </span>
            </button>
          ))}
        </div>
        <button className="scenes-page__carousel-arrow scenes-page__carousel-arrow--right" type="button" onClick={() => moveSelection(1)} aria-label="Next scene"><FiChevronRight /></button>
        <div className="scenes-page__pagination" aria-hidden="true">
          {scenes.map((scene) => <i key={scene.id} className={scene.id === selectedId ? 'is-active' : ''} />)}
        </div>
      </section>

      <section
        ref={journeyRef}
        className={`scenes-page__panel scenes-page__story ${journeyVisible ? 'is-visible' : ''}`}
        aria-labelledby="journey-title"
      >
        <span className="scenes-page__story-glow scenes-page__story-glow--gold" aria-hidden="true" />
        <span className="scenes-page__story-glow scenes-page__story-glow--blue" aria-hidden="true" />
        <div className="scenes-page__story-header">
          <p className="scenes-page__story-eyebrow">
            <span aria-hidden="true" />
            <span lang="fr">Une session, de l’intérieur</span>
            <small lang="en">One session, from within</small>
          </p>
          <SectionTitle
            id="journey-title"
            titleFr={<>Comment ça <strong>fonctionne</strong></>}
            titleEn={<>How it <strong>works</strong></>}
            variant="editorial"
          />
          <p className="scenes-page__story-intro">
            <span lang="fr">Explorez chaque moment. Voyez comment la scène s’adapte, vous fait parler et transforme l’expérience en élan.</span>
            <small lang="en">Explore every moment. See how the scene adapts, gets you speaking and turns the experience into momentum.</small>
          </p>
        </div>

        <ol
          className="journey-rail"
          aria-label="Étapes de la session / Session steps"
          style={{ '--journey-progress': `${(activeJourneyIndex / (journey.length - 1)) * 100}%` }}
        >
          {journey.map((step, index) => (
            <li
              className={activeJourneyStep.id === step.id ? 'is-active' : ''}
              data-journey-step={step.id}
              key={step.id}
            >
              <button
                id={`journey-trigger-${step.id}`}
                type="button"
                onClick={() => setActiveJourneyId(step.id)}
                aria-controls="journey-detail"
                aria-current={activeJourneyStep.id === step.id ? 'step' : undefined}
                aria-pressed={activeJourneyStep.id === step.id}
              >
                <span className="journey-rail__number" aria-hidden="true">
                  <i>{String(index + 1).padStart(2, '0')}</i>
                  {React.createElement(step.icon)}
                </span>
                <span className="journey-rail__copy">
                  <span className="journey-rail__kicker">
                    <span lang="fr">{step.kicker}</span> · <small lang="en">{step.kickerEn}</small>
                  </span>
                  <strong lang="fr">{step.title}</strong>
                  <small lang="en">{step.titleEn}</small>
                </span>
                <FiArrowRight className="journey-rail__arrow" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ol>

        <div
          className="journey-stage"
          id="journey-detail"
          role="region"
          aria-labelledby={`journey-detail-title-${activeJourneyStep.id}`}
          key={activeJourneyStep.id}
        >
          <div className={`journey-stage__media journey-stage__media--${activeJourneyStep.id}`}>
            <img
              src={activeJourneyStep.image}
              alt={activeJourneyStep.imageAlt}
              width="1600"
              height="1000"
              decoding="async"
            />
            <span className="journey-stage__shade" aria-hidden="true" />
            <div className="journey-stage__scene-label">
              <span>{React.createElement(activeJourneyStep.icon, { 'aria-hidden': true })}</span>
              <span>
                <small lang="fr">Étape {String(activeJourneyIndex + 1).padStart(2, '0')}</small>
                <b lang="fr">{activeJourneyStep.kicker}</b>
                <em lang="en">{activeJourneyStep.kickerEn}</em>
              </span>
              <i><span /></i>
            </div>
            <JourneyVisualOverlay
              activeStepId={activeJourneyStep.id}
              activeDialogue={activeDialogue}
              helpEnabled={helpEnabled}
              onDialogueChange={setActiveDialogue}
              onToggleHelp={() => setHelpEnabled((value) => !value)}
            />
          </div>

          <div className="journey-stage__copy">
            <div className="journey-stage__copy-topline">
              <span aria-hidden="true">{React.createElement(activeJourneyStep.icon)}</span>
              <p>
                <span lang="fr">{activeJourneyStep.title}</span>
                <small lang="en">{activeJourneyStep.titleEn}</small>
              </p>
            </div>
            <h3 id={`journey-detail-title-${activeJourneyStep.id}`}>
              <span lang="fr">{activeJourneyStep.insideTitle}</span>
              <small lang="en">{activeJourneyStep.insideTitleEn}</small>
            </h3>
            <p className="journey-stage__lead" lang="fr">{activeJourneyStep.insideText}</p>
            <p className="journey-stage__lead-en" lang="en">{activeJourneyStep.insideTextEn}</p>
            <ul className="journey-stage__highlights">
              {activeJourneyStep.highlights.map(([fr, en], index) => (
                <li key={fr} style={{ '--highlight-index': index }}>
                  <span><FiCheck aria-hidden="true" /></span>
                  <p><b lang="fr">{fr}</b><small lang="en">{en}</small></p>
                </li>
              ))}
            </ul>
            <div className="journey-stage__promise">
              <span aria-hidden="true"><FiActivity /></span>
              <p>
                <b lang="fr">Toujours dans le contexte. Jamais dans l’évaluation.</b>
                <small lang="en">Always in context. Never in test mode.</small>
              </p>
            </div>
          </div>
        </div>
      </section>

      <FinalCtaPanel
        headingId="scenes-cta-title"
        titleFr="Passez d’une langue dormante à une langue active."
        titleEn="Move from a dormant language to an active one."
        bodyFr="Explorez une scène et réactivez vos réflexes en contexte, en quelques minutes."
        bodyEn="Explore a scene and reactivate your instincts in context—in just a few minutes."
        href={APP_ENTRY_HREF}
        label="Explorer les scènes"
        labelEn="Explore scenes"
        activatingLabel="Ouverture des scènes…"
        activatingLabelEn="Opening scenes…"
      />
    </main>
  )
}

export default Scenes
